import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';
import TextField from '@mui/material/TextField';

import { colors, devices } from 'styles';

const ERROR_COLOR = '#E5484D';
const BORDER_IDLE = 'rgba(255, 255, 255, 0.25)';
const BORDER_HOVER = 'rgba(255, 255, 255, 0.45)';
const LABEL_IDLE = 'rgba(255, 255, 255, 0.6)';

export const ModalContent = styled(Box)`
  position: relative;
  width: calc(100% - 24px);
  max-width: 940px;
  max-height: calc(100vh - 24px);
  overflow-y: auto;
  background-color: ${colors.mainDark};
  border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6);
  outline: none;
  padding: 32px 20px;

  @media (${devices.sm}) {
    padding: 48px;
  }
`;

export const WebDecoration = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 320px;
  max-width: 45%;
  pointer-events: none;
  user-select: none;
  z-index: 0;

  & img {
    display: block;
    width: 100%;
    height: auto;
    opacity: 0.5;
  }
`;

export const CloseButton = styled('button')`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background-color: transparent;
  color: ${colors.white};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover,
  &:focus-visible {
    background-color: rgba(255, 255, 255, 0.12);
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue};
    outline-offset: 2px;
  }

  & .MuiSvgIcon-root {
    font-size: 24px;
  }
`;

export const Title = styled(Typography)`
  position: relative;
  z-index: 1;
  color: ${colors.white};
  font-weight: 700;
  font-size: 24px;
  line-height: 125%;
  white-space: pre-line;
  margin: 0 40px 0 0;

  @media (${devices.sm}) {
    font-size: 32px;
  }
`;

export const Form = styled('form')`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 32px;

  @media (${devices.sm}) {
    grid-template-columns: 1fr 1fr;
    gap: 20px 24px;
  }
`;

export const FullWidthRow = styled(Box)`
  @media (${devices.sm}) {
    grid-column: 1 / -1;
  }
`;

export const Field = styled(TextField)`
  width: 100%;

  & .MuiInputBase-root {
    color: ${colors.white};
    border-radius: 8px;
  }

  & .MuiOutlinedInput-notchedOutline {
    border-color: ${BORDER_IDLE};
  }

  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${BORDER_HOVER};
  }

  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${colors.blue};
    border-width: 1px;
  }

  & .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline {
    border-color: ${ERROR_COLOR};
  }

  & .MuiInputLabel-root {
    color: ${LABEL_IDLE};
  }

  & .MuiInputLabel-root.Mui-focused {
    color: ${colors.blue};
  }

  & .MuiInputLabel-root.Mui-error {
    color: ${ERROR_COLOR};
  }

  & .MuiFormHelperText-root.Mui-error {
    color: ${ERROR_COLOR};
    margin-left: 4px;
  }

  & .MuiSvgIcon-root {
    color: ${LABEL_IDLE};
  }

  & textarea::placeholder,
  & input::placeholder {
    color: ${LABEL_IDLE};
    opacity: 1;
  }
`;

export const TimelineLabel = styled(Typography)`
  position: relative;
  z-index: 1;
  color: ${colors.white};
  font-size: 16px;
  line-height: 150%;
`;

export const ErrorText = styled(Typography)`
  margin-top: 8px;
  margin-left: 4px;
  color: ${ERROR_COLOR};
  font-size: 12px;
  line-height: 150%;
`;

export const PillsRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
`;

interface PillProps {
  $active: boolean;
}

export const Pill = styled('button')<PillProps>`
  padding: 12px 24px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? colors.blue : BORDER_IDLE)};
  background-color: ${({ $active }) => ($active ? colors.blue : 'transparent')};
  color: ${colors.white};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${({ $active }) => ($active ? colors.blueHover : BORDER_HOVER)};
    background-color: ${({ $active }) => ($active ? colors.blueHover : 'transparent')};
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue};
    outline-offset: 2px;
  }
`;
